import { useCallback, useId, useMemo, useState } from "react";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import {
  RestrictionCancelEnum,
  type RestrictionCancel,
} from "@b2bportal/ground-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { CarAttributePill } from "@tenant/cars";
import { Dialog, DialogContent, Icon } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./CancellationPill.module.scss";
import { useCarsStyles } from "~core-cars/themes";
import { CoreCarsComponent } from "~core-cars/types";

export type CancellationPillProps = PlatformComponentProps & {
  cancellationPolicy: RestrictionCancel;
};

const COMPONENT_KEY = CoreCarsComponent.CancellationPill;

export const CancellationPill = ({
  cancellationPolicy,
  className,
  children,
}: CancellationPillProps) => {
  const styles = useCarsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t } = useI18nContext();
  const [freeCancelModalOpen, setFreeCancelModalOpen] = useState(false);
  const freeCancelDialogId = useId();

  const handleFreeCancelModalOpen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setFreeCancelModalOpen(true);
  }, []);

  const pill = useMemo(() => {
    switch (cancellationPolicy.RestrictionCancel) {
      case RestrictionCancelEnum.Free:
        return (
          <Dialog
            open={freeCancelModalOpen}
            onOpenChange={setFreeCancelModalOpen}
          >
            <button
              type="button"
              onClick={(e) => handleFreeCancelModalOpen(e)}
              className={cn("info-button")}
              aria-label={t("core-cars.freeCancellation.infoAriaLabel")}
              aria-haspopup="dialog"
              aria-expanded={freeCancelModalOpen}
              aria-controls={freeCancelDialogId}
              data-state={freeCancelModalOpen ? "open" : "closed"}
            >
              <CarAttributePill
                className={cn("Free")}
                icon={<Icon iconName={IconNameEnum["car__rentalAttribute"]} />}
              >
                <span className={cn("free-cancellation-content")}>
                  {t("core-cars.cancellation.free")}
                  <Icon iconName={IconNameEnum.info} className={cn("icon")} />
                </span>
              </CarAttributePill>
            </button>
            <DialogContent
              id={freeCancelDialogId}
              title={t("core-cars.freeCancellation.modalTitle")}
              className={cn("modal")}
            >
              <p className={cn("modal-description")}>
                {t("core-cars.freeCancellation.modalDescription")}
              </p>
            </DialogContent>
          </Dialog>
        );
      case RestrictionCancelEnum.Conditional: {
        return (
          <CarAttributePill
            className={cn("Conditional")}
            icon={<Icon iconName={IconNameEnum["car__rentalAttribute"]} />}
          >
            {cancellationPolicy.description}
          </CarAttributePill>
        );
      }
      case RestrictionCancelEnum.ConditionalAfterBooking:
        return (
          <CarAttributePill
            className={cn("ConditionalAfterBooking")}
            icon={<Icon iconName={IconNameEnum["car__rentalAttribute"]} />}
          >
            {cancellationPolicy.description}
          </CarAttributePill>
        );
      case RestrictionCancelEnum.NonRefundable:
      case RestrictionCancelEnum.Paid:
        return null;
      case RestrictionCancelEnum.SteppedCancellationRule:
        return (
          <CarAttributePill
            className={cn("SteppedCancellationRule")}
            icon={<Icon iconName={IconNameEnum["car__rentalAttribute"]} />}
          >
            {t("core-cars.cancellation.steppedCancellation")}
          </CarAttributePill>
        );
      default:
        return ensureExhaustive(cancellationPolicy, null);
    }
  }, [
    cancellationPolicy,
    cn,
    t,
    freeCancelModalOpen,
    freeCancelDialogId,
    handleFreeCancelModalOpen,
  ]);

  if (pill == null) {
    return null;
  }

  return (
    <div className={clsx(block, className)}>
      {pill}
      {children}
    </div>
  );
};
